import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { CheckCircle, Loader2, AlertCircle, Car, ArrowUpRight, Printer, Share2 } from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import { confirmationLine } from '../lib/airportBooking';
import { str, num } from '../lib/safe';
import { BOOK_PATH } from '../lib/routes';

const LOGO = "https://customer-assets.emergentagent.com/job_buraq-transport/artifacts/0a9wh5vw_0E081036-6309-4011-9127-0A9FE497D0F8.png";

// "Your ride is scheduled for Oct 20, 4:30 PM" for a ride booked ahead; null otherwise, and never a crash on an odd date.
const safeConfirmation = (meta) => {
  try { return confirmationLine(str(meta?.scheduled_date), str(meta?.scheduled_time)); } catch (e) { return null; }
};

const loadRide = async (reference) => {
  try {
    const token = localStorage.getItem('rideburaq_token');
    const res = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/rides/${encodeURIComponent(reference)}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    const j = res.ok ? await res.json() : null;
    return j && typeof j === 'object' ? j : null;
  } catch (e) { return null; }
};

// The ride, in the shape this page shows: the amount paid and the trip lines.
export const rideSummary = (ride) => {
  const at = str(ride.scheduledAt || ride.scheduled_at);
  const m = /^(\d{4}-\d{2}-\d{2})[T ](\d{2}:\d{2})/.exec(at);
  return { amount: num(ride.grandTotal ?? ride.quotedTotal), metadata: { ride_type: str(ride.vehicleClassName || ride.vehicleClass), pickup_address: str(ride.pickupAddress), dropoff_address: str(ride.dropoffAddress), scheduled_date: m ? m[1] : '', scheduled_time: m ? m[2] : '' } };
};

const PaymentSuccess = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [status, setStatus] = useState('checking');
  const [paymentData, setPaymentData] = useState(null);
  const sessionId = searchParams.get('session_id');

  const paidRef = useRef(false);                 // once paid and loaded, stop asking
  const checkPaymentStatus = useCallback(async () => {
    if (paidRef.current) return;
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/payments/checkout/status/${sessionId}`);
      if (!response.ok) throw new Error('Failed to check payment status');
      const raw = await response.json();
      const data = raw && typeof raw === 'object' ? raw : {};
      // The status route says only paid / not paid and the booking reference. The trip and the amount come from the rider's own ride (signed in).
      const ride = data.payment_status === 'paid' && data.reference ? await loadRide(data.reference) : null;
      setPaymentData(ride ? { ...data, ...rideSummary(ride) } : data);
      if (data.payment_status === 'paid') {
        paidRef.current = true;
        setStatus('success');
        toast({ title: 'Payment Successful!', description: 'Your ride has been booked.', duration: 5000 });
      } else if (data.payment_status === 'failed' || data.payment_status === 'expired') {
        setStatus('failed');
      }
    } catch (error) {
      setStatus('error');
    }
  }, [sessionId, toast]);

  useEffect(() => {
    if (!sessionId) {
      setStatus('error');
      return;
    }
    checkPaymentStatus();
    const pollInterval = setInterval(checkPaymentStatus, 3000);
    const timeout = setTimeout(() => clearInterval(pollInterval), 30000);
    return () => {
      clearInterval(pollInterval);
      clearTimeout(timeout);
    };
  }, [sessionId, checkPaymentStatus]);

  const primaryBtn = "w-full rounded-full py-3.5 text-base font-semibold text-white bg-gradient-to-r from-orange-500 to-orange-600 shadow-lg shadow-orange-900/30 hover:shadow-orange-500/30 active:scale-[0.99] transition-all flex items-center justify-center";
  const ghostBtn = "w-full rounded-full py-3.5 text-sm font-medium text-gray-300 border border-white/10 hover:bg-white/[0.03] active:scale-[0.99] transition-all";

  const handlePrint = () => window.print();

  const handleShare = async () => {
    const amt = num(paymentData?.amount) ? `$${num(paymentData?.amount).toFixed(2)}` : '';
    const shareData = {
      title: 'RideBuraq Receipt',
      text: `RideBuraq ride confirmed — ${amt}${str(paymentData?.metadata?.pickup_address) ? ` from ${str(paymentData.metadata.pickup_address)}` : ''}.`,
      url: window.location.href,
    };
    try {
      if (navigator.share) {
        await navigator.share(shareData);
      } else {
        await navigator.clipboard.writeText(`${shareData.text} ${shareData.url}`);
        toast({ title: 'Receipt link copied', description: 'Share it from anywhere.', duration: 3000 });
      }
    } catch (e) {
      console.debug('share dismissed or unavailable', e);
    }
  };

  const renderContent = () => {
    switch (status) {
      case 'checking':
        return (
          <>
            <div className="w-16 h-16 rounded-full border border-white/10 flex items-center justify-center mx-auto mb-5">
              <Loader2 className="h-7 w-7 text-orange-400 animate-spin" />
            </div>
            <h1 className="text-2xl font-semibold text-center mb-2">Verifying Payment</h1>
            <p className="text-gray-500 text-center text-sm">Confirming your payment and booking your ride...</p>
          </>
        );
      case 'success':
        return (
          <>
            <div id="receipt" className="receipt-print">
              <div className="w-16 h-16 rounded-full bg-emerald-500/10 border border-emerald-400/30 flex items-center justify-center mx-auto mb-5">
                <CheckCircle className="h-8 w-8 text-emerald-400" />
              </div>
              <h1 className="text-2xl font-semibold text-center mb-2">Ride Confirmed</h1>
              <p className="text-gray-500 text-center text-sm mb-6">
                ${num(paymentData?.amount).toFixed(2)} paid — {safeConfirmation(paymentData?.metadata) || 'your luxury ride is booked.'}
              </p>
              <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4 mb-6 space-y-3">
                {paymentData?.metadata && typeof paymentData.metadata === 'object' && (
                  <>
                    <div className="flex items-center gap-2 text-sm">
                      <Car className="h-4 w-4 text-orange-400" />
                      <span className="text-gray-500">Class</span>
                      <span className="ml-auto text-white capitalize">{str(paymentData.metadata.ride_type).replace(/_/g, ' ')}</span>
                    </div>
                    <div className="flex items-start gap-2 text-sm">
                      <span className="text-gray-500 shrink-0">From</span>
                      <span className="ml-auto text-white text-right">{str(paymentData.metadata.pickup_address)}</span>
                    </div>
                    <div className="flex items-start gap-2 text-sm">
                      <span className="text-gray-500 shrink-0">To</span>
                      <span className="ml-auto text-white text-right">{str(paymentData.metadata.dropoff_address)}</span>
                    </div>
                  </>
                )}
                <div className="flex items-center gap-2 text-sm border-t border-white/10 pt-3">
                  <span className="text-gray-500">Total paid</span>
                  <span className="ml-auto text-white font-semibold">${num(paymentData?.amount).toFixed(2)}</span>
                </div>
                <div className="flex items-center gap-2 text-[11px]">
                  <span className="text-gray-500">Confirmation</span>
                  <span className="ml-auto text-gray-400 font-mono">{str(sessionId).slice(-12)}</span>
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mb-3">
              <button data-testid="receipt-print-btn" onClick={handlePrint} className={ghostBtn + ' flex items-center justify-center gap-2'}>
                <Printer className="h-4 w-4" /> Print
              </button>
              <button data-testid="receipt-share-btn" onClick={handleShare} className={ghostBtn + ' flex items-center justify-center gap-2'}>
                <Share2 className="h-4 w-4" /> Share
              </button>
            </div>
            <div className="space-y-3">
              <button data-testid="view-rides-btn" onClick={() => navigate(str(paymentData?.reference) ? `/ride/${encodeURIComponent(str(paymentData.reference))}` : '/rides')} className={primaryBtn}>
                View Ride Details <ArrowUpRight className="ml-1.5 h-4 w-4" />
              </button>
              <button onClick={() => navigate(BOOK_PATH)} className={ghostBtn}>Book Another Ride</button>
            </div>
          </>
        );
      case 'failed':
        return (
          <>
            <div className="w-16 h-16 rounded-full bg-red-500/10 border border-red-400/30 flex items-center justify-center mx-auto mb-5">
              <AlertCircle className="h-8 w-8 text-red-400" />
            </div>
            <h1 className="text-2xl font-semibold text-center mb-2">Payment Failed</h1>
            <p className="text-gray-500 text-center text-sm mb-6">We couldn&apos;t process your payment. Please try again.</p>
            <div className="space-y-3">
              <button onClick={() => navigate(BOOK_PATH)} className={primaryBtn}>Try Again</button>
              <button onClick={() => navigate('/dashboard')} className={ghostBtn}>Back to Dashboard</button>
            </div>
          </>
        );
      default:
        return (
          <>
            <div className="w-16 h-16 rounded-full border border-white/10 flex items-center justify-center mx-auto mb-5">
              <AlertCircle className="h-8 w-8 text-gray-400" />
            </div>
            <h1 className="text-2xl font-semibold text-center mb-2">Something Went Wrong</h1>
            <p className="text-gray-500 text-center text-sm mb-6">We couldn&apos;t verify your payment. Contact support if you were charged.</p>
            <button onClick={() => navigate('/dashboard')} className={primaryBtn}>Back to Dashboard</button>
          </>
        );
    }
  };

  return (
    <div className="min-h-screen bg-black text-white relative overflow-hidden flex items-center justify-center px-5">
      <div className="pointer-events-none absolute -top-24 left-1/2 -translate-x-1/2 w-[520px] h-[520px] rounded-full bg-orange-600/15 blur-[120px]" />
      <div className="relative w-full max-w-md">
        <img src={LOGO} alt="RideBuraq" className="w-12 h-12 object-contain mx-auto mb-8" />
        <div className="rounded-3xl border border-white/10 bg-white/[0.02] p-7">
          {renderContent()}
        </div>
      </div>
    </div>
  );
};

export default PaymentSuccess;
